/**
 * N standalone cards, each as tall as its own content.
 *
 * `align-items: start` is load-bearing. The previous grid stretched its rows
 * and then asked each tray for `height: 100%`, which is the second half of the
 * mechanism that produced inert empty plates: a card with four fields was
 * stretched to the height of a card with nine and painted the difference as
 * blank plate. Ragged bottoms are the honest outcome of content-height cards.
 * If they ever read badly, the answer is a card composition with a consistent
 * region count — not a re-imposed height.
 */
"use client";
import type { ReactElement, ReactNode } from "react";

const CARD_GRID_GAP = 12;

interface CresultcardCardGridProps {
  readonly children: ReactNode;
  /** The narrowest a card may get before the grid drops to fewer columns. */
  readonly cardMin: number;
}

export function CresultcardCardGrid({
  children,
  cardMin,
}: CresultcardCardGridProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/CardGrid/Root"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${String(cardMin)}px), 1fr))`,
        alignItems: "start",
        gap: CARD_GRID_GAP,
        width: "100%",
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}

interface CresultcardCardCarrierProps {
  readonly children: ReactNode;
}

/**
 * The hover lift lives on a carrier around the tray, never on the tray itself,
 * so it cannot fight the plate's own overflow clip.
 */
export function CresultcardCardCarrier({
  children,
}: CresultcardCardCarrierProps): ReactElement {
  return (
    <div
      className="cresultcard__lift"
      data-el="Cresultcard/CardCarrier/Root"
      style={{ width: "100%", minWidth: 0 }}
    >
      {children}
    </div>
  );
}
